import { BCMSCurrency, DisabledPrompt, useBCMSModal, useStateManager } from "@bcm-dev/bcms-vite";
import CustomSelect, { CustomSelectItem } from "components/custom-ui/CustomSelect";
import DateSelect from 'components/custom-ui/DateSelect';
import { TableBody, TableView } from 'components/shared';
import { PrimaryButton, SecondaryButton, UIAmountInput, UINumberInput, UITextInput } from 'components/ui';
import React, { useEffect } from 'react';
import { SVGAsset } from "res";

/**
 * @function TASPositivePayViewChecksDesktopTable
 * @return {React.Component}
 * @exported true
 * @description The "TAS Positive Pay View Checks" no content view.
 */
export default function TASPositivePayViewChecksDesktopTable(props) { 
  const { data, ui, onCancelClick, onSaveClick } = useStateManager();
  const checkForms = data.checkForms;
  const checkFormsUI = ui.checkForms;
  const userEditedCheckForms = data.search.userEditedCheckForms().length > 0;
  const { open } = useBCMSModal();

  const rows = checkForms.reduce((acc, item, index) => {
    const amount = item.amount ? item.amount.amount : undefined;
    const isEditing = checkFormsUI[index].isEditing;
    const editItem = data.search.editCheckForms.filter(_item => _item.id === item.id)[0];
    
    acc.push(
      isEditing
      ? <EditingRow key={ item.id } amount={ amount  } item={ item } editItem={ editItem }/>
      : <Row key={ item.id } amount={ amount  } item={ item }/>
    );
    return acc;
  }, []);

  const onYesClick = evt => {
    onCancelClick();
  }

  const openCancelModal = val => {
    open("TASPositivePayCancelEditChangeModal", { onYesClick: onYesClick } );
  };

  useEffect(() => {
    if(ui.search.cancelModal.isActive) {
      open("TASPositivePayCancelEditChangeModal", { onYesClick: onYesClick } );
    }
  }, []);

  return (
      <div className="flex-container flex-vertical table-view-container show-on-desktop">
        <TableView className="tas-positive-pay-view-check-table">
            <thead className="table-column-container">
            <tr>
                <th className="text-left table-column-view">Account</th>
                <th className="text-left table-column-view">Issued Date</th>
                <th className="text-left table-column-view">Check Number</th>
                <th className="text-left table-column-view">Amount</th>
                <th className="text-left table-column-view">Payee</th>
                <th className="text-left table-column-view">Reference Number</th>
                <th className="text-left table-column-view">Type</th>
                <th className="text-left table-column-view">Status</th>
                <th className="text-left table-column-view"></th>
                <th className="text-left table-column-view"></th>
            </tr>
            </thead>
            <TableBody>{ rows } </TableBody>
        </TableView>
       { (userEditedCheckForms || ui.search.saveAndCancelButtons.isActive) &&
       <div className="flex-container flex-horizontal button-container">
            <SecondaryButton className="set-width" text="Cancel" dataPoint="TAS_Positive_Pay_View_Checks_Cancel_Button" onClick={ openCancelModal } />
            <PrimaryButton className="set-width" text="Save" dataPoint="TAS_Positive_Pay_View_Checks_Save_Button" onClick={ onSaveClick } />
        </div>
        }
      </div>
  );
}

/**
 * @function Row
 * @return {React.Component}
 * @exported false
 * @description A row that can only be read by the user.
 */
function Row(props) {
  const { onChange, onEditClick } = useStateManager();
  const item = props.item ?? {};
  const amount = props.amount ?? "";
  const canDelete = item.canDelete;

  return (
    <tr key={ item.id } className="table-item-container">
      <td className="table-item-view">
        <span data-point={ `TAS_Positive_Pay_View_Checks_Desktop_Row_Account_${ item.id }`}>{ `${ item.account.name } ${ item.account.number }`}</span>
      </td>
      <td className="table-item-view">
        <span data-point={ `TAS_Positive_Pay_View_Checks_Desktop_Row_Issued_Date_${ item.id }`}>{ `${ item.issuedDate.format("m/d/Y") }` }</span>
      </td>
      <td className="table-item-view">
        <span data-point={ `TAS_Positive_Pay_View_Checks_Desktop_Row_Check_Number_${ item.id }`}>{ `${ item.checkNumber }` }</span>
      </td>
      <td className="table-item-view">
        <span data-point={ `TAS_Positive_Pay_View_Checks_Desktop_Row_Amount_${ item.id }`}>{ `${ BCMSCurrency.init(amount).format() }` }</span>
      </td>
      <td className="table-item-view">
        <span data-point={ `TAS_Positive_Pay_View_Checks_Desktop_Row_Payee_${ item.id }`}>{ `${ item.payee }` }</span>
      </td>
      <td className="table-item-view">
        <span data-point={ `TAS_Positive_Pay_View_Checks_Desktop_Row_Reference_Number_${ item.id }`}>{ `${ item.referenceNumber }` }</span>
      </td>
      <td className="table-item-view capitalized-item-view">
        <span data-point={ `TAS_Positive_Pay_View_Checks_Desktop_Row_Type_${ item.id }`}>{ `${ item.type }` }</span>
      </td>
      <td className="table-item-view capitalized-item-view">
        <span data-point={ `TAS_Positive_Pay_View_Checks_Desktop_Row_Status_${ item.id }`}>{ `${ item.status }` }</span>
      </td>  
      <td className="table-item-view">
        <SVGAsset id="EditIcon" className="clickable" data-point={ `TAS_Positive_Pay_View_Checks_Desktop_Row_Edit_Button_${ item.id }`}
        onClick={ onEditClick(item.id) } />
      </td>
      <td className="table-item-view">
        { canDelete && 
        <DisabledPrompt>
            <SVGAsset id="TrashIcon" className="clickable" data-point={ `TAS_Positive_Pay_View_Checks_Desktop_Row_Delete_Button_${ item.id }`} />
        </DisabledPrompt>
        }
      </td>
    </tr>
  );
}

/**
 * @function EditingRow
 * @return {React.Component}
 * @exported false
 * @description A row that can be edited by the user.
 */
function EditingRow(props) {
  const { data, ui, onEdit } = useStateManager();
  const item = props.editItem ?? {};
  const uiItem = ui.checkForms.filter(_item => _item.id === item.id)[0];
  const amount = props.amount ?? "";
  const search = data.search;
  const accountItems = search.accounts.reduce((acc, item) => {
      acc.push(
          <CustomSelectItem key={ item.id } value={ item.id }>{ `${ item.name } ${ item.number }` }</CustomSelectItem>
      )
      return acc;
  }, []);

  return (
    <tr key={ item.id } className="table-item-container">
      <td className="table-item-view">
        <CustomSelect active={ uiItem.account.isActive } value={ item.account.id } 
        dataPoint={ `TAS_Positive_Pay_View_Checks_Desktop_Edit_Row_Account_${ item.id }`}
        onChange={ onEdit(item.id, "account") } >
            { accountItems }
        </CustomSelect>
      </td>
      <td className="table-item-view">
        <DateSelect value={ item.issuedDate } active={ uiItem.issuedDate.isActive } enablePast={ true }
        dataPoint={ `TAS_Positive_Pay_View_Checks_Desktop_Edit_Row_Issued_Date_${ item.id }`}
        onChange={ onEdit(item.id, "issuedDate") }  />
      </td>
      <td className="table-item-view">
        <UINumberInput value={ `${ item.checkNumber }` }
        dataPoint={ `TAS_Positive_Pay_View_Checks_Desktop_Edit_Row_Check_Number_${ item.id }`}
        onChange={ onEdit(item.id, "checkNumber") } />
      </td>
      <td className="table-item-view">
        <UIAmountInput className="amount-field" value={ amount }
        dataPoint={ `TAS_Positive_Pay_View_Checks_Desktop_Edit_Row_Amount_${ item.id }`}
        onChange={ onEdit(item.id, "amount") } />
      </td>
      <td className="table-item-view">
        <UITextInput value={ item.payee }
        dataPoint={ `TAS_Positive_Pay_View_Checks_Desktop_Edit_Row_Payee_${ item.id }`}
        onChange={ onEdit(item.id, "payee") } />
      </td>
      <td className="table-item-view">
        <UITextInput value={ item.referenceNumber }
        dataPoint={ `TAS_Positive_Pay_View_Checks_Desktop_Edit_Row_Reference_Number_${ item.id }`}
        onChange={ onEdit(item.id, "referenceNumber") } />
      </td>
      <td className="table-item-view">
        <CustomSelect active={  uiItem.type.isActive } value={ item.type }
        dataPoint={ `TAS_Positive_Pay_View_Checks_Desktop_Edit_Row_Type_${ item.id }`}
        onChange={ onEdit(item.id, "type") }>
            <CustomSelectItem value="issue">Issue</CustomSelectItem>
            <CustomSelectItem value="stop">Stop</CustomSelectItem>
            <CustomSelectItem value="void">Void</CustomSelectItem>
        </CustomSelect>
      </td>
      <td className="table-item-view"></td>  
      <td className="table-item-view"></td>
      <td className="table-item-view"> </td>
    </tr>
  );
}